/**
 * Die Farbregel der Feldkataloge — an EINER Stelle (Vertiefungsplan V-P1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WAS DIE FARBEN SAGEN — UND WAS SIE NICHT SAGEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   HELLGRÜN   Das FELD geht beim Rückschreiben ins Modell zurück.
 *   HELLGELB   Der Wert des FELDES bleibt in der Datenbank.
 *
 * Bis zum 22.09.2026 stand hier „das FELD hat einen Revit-Parameter". Das
 * ist die MÖGLICHKEIT und nicht die FESTLEGUNG, und die beiden gehen
 * auseinander: Gemessen tragen Felder einen Parameter, aus dem nur GELESEN
 * wird — Größe, Systemname, Ebene, der Identitätsanker. Die Antwort kommt
 * deshalb fertig aus dem Rechenkern (`Rueckschreibweg.GehtInsModell`) und
 * wird hier nur noch in eine Farbe übersetzt; wer sie aus `revitParameter`
 * selbst zusammensetzt, baut die nächste Fassung einer Regel, von der
 * dieses Projekt neunzehn hatte (Pakete PB48–PB53).
 *
 * Die Aussage gilt dem FELD und nicht dem WERT. Ein hellgrünes Feld kann
 * einen errechneten Wert tragen, ein hellgelbes einen von Hand gepflegten.
 * Wer die Farben als Herkunft des Wertes liest, liest sie falsch — und genau
 * das ist bisher geschehen: Die Meldelinienseite der Elektro färbt nach
 * Herkunft des WERTES ("aus dem Revit-Modell gelesen" gegen "in der Plattform
 * gepflegt"), die Heizungsseite nach der Farbe des FELDES, und beide
 * benutzen dieselben zwei Farben.
 *
 * Deshalb steht die Regel hier als Bauteil und nicht als Absprache. Wer eine
 * dritte Seite baut, nimmt sie und erfindet keine vierte Lesart.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIE KLASSEN UND NICHT DIE FARBWERTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `.modellfeld` und `.datenfeld` stehen seit Langem in styles.css und tragen
 * ihre Farben aus den Variablen `--modellfeld` / `--datenfeld`. Wer hier
 * Farbwerte einsetzte, hätte sie doppelt — und beim nächsten Wechsel der
 * Hausfarbe eine Seite, die nicht mitgeht.
 */

/**
 * Die CSS-Klasse zu einem Feld.
 *
 * Der Parameter heißt `gehtInsModell` und nicht `istSchreibbar`: Die
 * Festlegung allein genügt nicht — es braucht auch einen Zielparameter und
 * es darf keine harte Schutzgrenze greifen. Diese drei setzt der Rechenkern
 * zusammen (`Rueckschreibweg.GehtInsModell`), und genau dessen Ergebnis
 * gehört hier hinein.
 */
export function farbklasse(gehtInsModell: boolean): 'modellfeld' | 'datenfeld' {
  return gehtInsModell ? 'modellfeld' : 'datenfeld';
}

/** Das Wort zu einem Feld — für Tabellen, Ausdrucke und Vorlesehilfen. */
export function farbwort(gehtInsModell: boolean): string {
  return gehtInsModell ? 'hellgrün' : 'hellgelb';
}

/** Was die Farbe bedeutet, in einem Satz. */
export function farbgrund(gehtInsModell: boolean): string {
  return gehtInsModell
    ? 'Modellfeld — wird beim Rückschreiben ins Modell übertragen'
    : 'Datenfeld — der Wert bleibt in der Datenbank';
}

/**
 * Ein einzelnes Feld in seiner Farbe.
 *
 * `title` steht immer dabei: Eine Farbe ohne Erklärung ist für jeden, der die
 * Seite zum ersten Mal sieht, eine Zierde.
 */
export function Feldfarbe({
  gehtInsModell, children, kompakt,
}: {
  /**
   * Die FERTIGE Antwort, nicht die Katalogspalte `IstSchreibbar` und nicht
   * `RevitParameter` — dieselbe Unterscheidung wie bei `farbklasse` oben.
   * Die Prop hieß bis PB53 `schreibbar`; ein Name, den jede neue Seite
   * abschreibt, trägt die Regel weiter, die er benennt.
   */
  gehtInsModell: boolean;
  children: React.ReactNode;
  kompakt?: boolean;
}) {
  return (
    <span
      className={farbklasse(gehtInsModell)}
      title={farbgrund(gehtInsModell)}
      style={kompakt ? { padding: '1px 6px' } : undefined}
    >
      {children}
    </span>
  );
}

/**
 * Die Legende. Zwei Einträge, immer dieselben Worte.
 *
 * Sie gehört auf JEDE Seite, die die beiden Farben benutzt — auch auf die,
 * auf der „das weiß doch jeder" gilt. Die Sanitärseiten zeigen die Farben
 * heute gar nicht; wer sie dort ergänzt, ergänzt die Legende mit.
 */
export function Farbregellegende({ senkrecht }: { senkrecht?: boolean } = {}) {
  return (
    <span
      className="legende"
      style={{
        display: 'flex',
        flexDirection: senkrecht ? 'column' : 'row',
        gap: senkrecht ? 4 : 10,
        fontSize: 11,
        alignItems: senkrecht ? 'flex-start' : 'center',
      }}
    >
      <Feldfarbe gehtInsModell kompakt>Modellfeld — rückschreibbar</Feldfarbe>
      <Feldfarbe gehtInsModell={false} kompakt>Datenfeld — nur Datenbank</Feldfarbe>
    </span>
  );
}

// --- Legende der Feldfarben (bis TV6c in seiten/Lueftung.tsx) ---------------

/**
 * Erklärt die Farben der Felder. Benutzt von der Lüftung, vom Raumbuch und
 * vom Türbuch - deshalb hier bei der Farbregel und nicht in einer der Seiten
 * (TV6c: Die Lüftungsseite zog nach `seiten/lueftung/`; Raumbuch und Türbuch
 * hätten sonst aus ihr importiert).
 *
 * Grün heißt: Das Feld geht beim Rückschreiben ins Modell zurück. Gelb heißt:
 * Der Wert bleibt in der Datenbank.
 *
 * Gelb hieß hier bis PB52 „kein Parameter im Modell". Das ist seit den
 * Migrationen 0729–0731 falsch: 17 Türfelder und sechs Lüftungsfelder tragen
 * einen Parameter, aus dem nur GELESEN wird. Ein fehlender Parameter ist nur
 * EINER der Gründe für Gelb — der andere ist eine Festlegung des
 * Fachplaners.
 */
export function Legende({ mitHerkunft }: { mitHerkunft?: boolean } = {}) {
  return (
    <div className="legende">
      <span><span className="probe modellfeld" /> Modellfeld — wird zurückgeschrieben</span>
      <span>
        <span className="probe datenfeld" /> nur Datenbank — kein Parameter im
        Modell oder nicht zum Schreiben freigegeben
      </span>
      {mitHerkunft && (
        <span>
          <span className="probe wert-abgeleitet" /> abgeleitet oder Standardwert der Raumart
          — nicht von Hand geprüft (siehe Tooltip)
        </span>
      )}
    </div>
  );
}
